
import { AiStreamingRequest, StreamingCallbacks } from './types';
import { ConnectionManager } from './connection-manager';
import { SSEParser } from './sse-parser';

export class StreamingClient {
  private static readonly REQUEST_TIMEOUT = 30000; // Reduced from 120s to 30s
  private static eventSequenceCounter = 0;

  private static getSupabaseUrl(): string {
    return 'https://lbvhywxvthwgiebjsrlr.supabase.co';
  }

  static async attemptStreaming(
    request: AiStreamingRequest,
    callbacks: StreamingCallbacks,
    abortController: AbortController
  ): Promise<void> {
    const supabaseUrl = this.getSupabaseUrl();
    const url = `${supabaseUrl}/functions/v1/ai-chat`;
    
    const requestBody = {
      messages: request.conversationHistory,
      topic: request.topic,
      session_id: request.sessionId,
      user_id: request.userId,
      language: request.language,
      track_analytics: true,
      requestId: request.requestId,
      includeMultiLanguage: request.includeMultiLanguage || false,
      mode: request.mode || 'conversation',
      stream: true,
      clientTimestamp: Date.now(),
      eventSequence: this.eventSequenceCounter
    };

    // Reduced logging for production performance
    if (process.env.NODE_ENV === 'development') {
      console.log('[STREAMING OPTIMIZED] Making request:', {
        requestId: request.requestId,
        messageCount: requestBody.messages.length
      });
    }

    const headers = {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imxidmh5d3h2dGh3Z2llYmpzcmxyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM3MzE4MjYsImV4cCI6MjA1OTMwNzgyNn0.SriuXDKuf0URQfJbAqQO-4r8Ghg0KVOWV6Lq99u86hU',
      'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imxidmh5d3h2dGh3Z2llYmpzcmxyIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDM3MzE4MjYsImV4cCI6MjA1OTMwNzgyNn0.SriuXDKuf0URQfJbAqQO-4r8Ghg0KVOWV6Lq99u86hU',
      'Accept': 'text/event-stream',
      'Cache-Control': 'no-cache',
      'Connection': 'keep-alive',
      'x-client-info': 'supabase-js-web/2.29.5',
      'x-request-id': request.requestId
    };

    // Faster timeout for better UX
    const timeoutId = setTimeout(() => {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[STREAMING OPTIMIZED] Request timeout:', { requestId: request.requestId });
      }
      abortController.abort();
    }, this.REQUEST_TIMEOUT);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
        signal: abortController.signal
      });

      clearTimeout(timeoutId);

      if (process.env.NODE_ENV === 'development') {
        console.log('[STREAMING OPTIMIZED] Response received:', {
          requestId: request.requestId,
          status: response.status,
          contentType: response.headers.get('content-type')
        });
      }

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      const contentType = response.headers.get('content-type') || '';
      
      // Simplified SSE detection - direct content-type check
      const isSSE = contentType.includes('text/event-stream') || contentType.includes('text/plain');
      
      if (!isSSE) {
        const textContent = await response.text();
        if (textContent && callbacks.onContent) {
          callbacks.onContent(textContent, textContent, request.requestId);
        }
        if (callbacks.onComplete) {
          callbacks.onComplete({
            requestId: request.requestId,
            messageId: crypto.randomUUID(),
            content: textContent,
            suggestions: [],
            model: 'gpt-4',
            usage: { promptTokens: 0, completionTokens: 0, totalTokens: 0 },
            knowledgeUsed: false,
            knowledgeResults: [],
            suggestionsSource: 'api',
            contextTruncated: false,
            queryExpansionUsed: false
          });
        }
        return;
      }

      if (!response.body) {
        throw new Error('No response body for streaming');
      }

      ConnectionManager.updateConnectionStatus(request.requestId, 'streaming');

      // Direct SSE parsing without enhanced detection overhead
      await SSEParser.parseSSEStream(response.body, callbacks, request.requestId, abortController);

    } catch (error) {
      clearTimeout(timeoutId);
      if (process.env.NODE_ENV === 'development') {
        console.error('[STREAMING OPTIMIZED] Request failed:', {
          requestId: request.requestId,
          error: error instanceof Error ? error.message : String(error)
        });
      }
      throw error;
    }
  }
}
